(() => {
    const footer = document.querySelector('.site-footer');
    if (!footer || footer.dataset.footerInitialized === 'true') return;
    footer.dataset.footerInitialized = 'true';

    footer.querySelectorAll('.site-footer__mobile-heading').forEach(button => {
      button.addEventListener('click', () => {
        const menu = button.closest('.site-footer__menu');
        const isOpen = menu.classList.toggle('is-open');

        button.setAttribute(
          'aria-expanded',
          isOpen ? 'true' : 'false'
        );
      });
    });

    const paletteCache = new Map();
    let updateQueued = false;
    let requestToken = 0;
    let lastSources = '';

    const artworkSelectors = [
      ['.ermusic__cover-image:not(.ermusic__placeholder)', 7000],
      ['.erdisc__cover', 6200],
      ['.erdisc__artwork img', 6150],
      ['.erdisc__release img', 6000],
      ['.product__media img', 5200],
      ['.product-media-container img', 5200],
      ['[data-product-media] img', 5000],
      ['.product-gallery img', 4800],
      ['.card__media img', 4100],
      ['.media img', 3600]
    ];

    const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

    const parseRgbVariable = value => {
      if (!value) return null;

      const parts = `${value}`
        .replace(/rgba?\(/gi, '')
        .replace(/\)/g, '')
        .replace(/\//g, ' ')
        .split(/[\s,]+/)
        .filter(Boolean)
        .slice(0, 3)
        .map(Number);

      if (parts.length < 3 || parts.some(item => !Number.isFinite(item))) return null;

      return parts.map(item => Math.round(clampNumber(item, 0, 255)));
    };

    const paletteFromAuraVariables = element => {
      if (!(element instanceof Element)) return null;

      const variablePairs = [
        ['--er-slide-primary', '--er-slide-secondary'],
        ['--er-article-primary', '--er-article-secondary'],
        ['--er-art-primary', '--er-art-secondary'],
        ['--er-feature-primary', '--er-feature-secondary'],
        ['--er-release-primary', '--er-release-secondary'],
        ['--er-service-primary', '--er-service-secondary'],
        ['--er-page-primary', '--er-page-secondary'],
        ['--er-aura-primary', '--er-aura-secondary'],
        ['--books-primary', '--books-secondary']
      ];

      let current = element;
      let depth = 0;

      while (current && current instanceof Element && depth < 7) {
        const style = getComputedStyle(current);

        for (const [primaryName, secondaryName] of variablePairs) {
          const primary = parseRgbVariable(style.getPropertyValue(primaryName));
          const secondary = parseRgbVariable(style.getPropertyValue(secondaryName));

          if (primary && secondary) return { primary, secondary, confidence: .82 };

          if (primary) {
            const fallbackSecondary = primary.map(
              channel => Math.round(Math.max(10, Math.min(236, (channel * .72) + 14)))
            );

            return {
              primary,
              secondary: fallbackSecondary,
              confidence: .76
            };
          }
        }

        current = current.parentElement;
        depth += 1;
      }

      return null;
    };

    const paletteFromCssGradient = backgroundImage => {
      if (!backgroundImage || backgroundImage === 'none') return null;

      const matches = [...backgroundImage.matchAll(/rgba?\(([^)]+)\)/gi)];

      const colors = matches
        .map(match => {
          const parts = match[1]
            .replace(/\//g, ' ')
            .split(/[\s,]+/)
            .filter(Boolean)
            .map(Number);

          if (parts.length < 3 || parts.slice(0, 3).some(item => !Number.isFinite(item))) return null;

          const alpha = parts.length > 3 && Number.isFinite(parts[3]) ? parts[3] : 1;
          if (alpha < .10) return null;

          const rgb = parts.slice(0, 3).map(item => clampNumber(item, 0, 255));
          const max = Math.max(...rgb);
          const min = Math.min(...rgb);
          const saturation = max === 0 ? 0 : (max - min) / max;
          const luminance = (.2126 * rgb[0]) + (.7152 * rgb[1]) + (.0722 * rgb[2]);

          if (luminance < 14 || luminance > 242 || saturation < .075) return null;

          return {
            color: rgb.map(Math.round),
            saturation,
            alpha,
            score: alpha * (.72 + (saturation * .55))
          };
        })
        .filter(Boolean)
        .sort((a, b) => b.score - a.score);

      if (!colors.length) return null;

      const primary = colors[0].color;
      let secondary = primary;

      for (const candidate of colors.slice(1)) {
        const distance = Math.hypot(
          primary[0] - candidate.color[0],
          primary[1] - candidate.color[1],
          primary[2] - candidate.color[2]
        );

        if (distance > 48) {
          secondary = candidate.color;
          break;
        }
      }

      if (secondary === primary) {
        secondary = primary.map(
          channel => Math.round(Math.max(10, Math.min(224, (channel * .76) + 8)))
        );
      }

      return {
        primary,
        secondary,
        confidence: clampNumber(.62 + Math.min(.14, colors.length * .025), .62, .82)
      };
    };

    const paletteFromCssColor = color => {
      if (!color || color === 'transparent') return null;

      const match = color.match(/rgba?\(([^)]+)\)/i);
      if (!match) return null;

      const parts = match[1]
        .replace(/\//g, ' ')
        .split(/[\s,]+/)
        .filter(Boolean)
        .map(Number);

      if (parts.length < 3 || parts.slice(0, 3).some(value => !Number.isFinite(value))) return null;

      const alpha = parts.length > 3 && Number.isFinite(parts[3]) ? parts[3] : 1;
      if (alpha < .18) return null;

      const rgb = parts.slice(0, 3).map(value => clampNumber(value, 0, 255));
      const max = Math.max(...rgb);
      const min = Math.min(...rgb);
      const chroma = max - min;
      const saturation = max === 0 ? 0 : chroma / max;
      const luminance = (.2126 * rgb[0]) + (.7152 * rgb[1]) + (.0722 * rgb[2]);

      /*
        Ignore neutral black/gray/white surfaces. Those are not useful Aura color
        sources and should leave the footer on its safe neutral fallback.
      */
      if (saturation < .09 || chroma < 16 || luminance < 16 || luminance > 240) return null;

      const primary = rgb.map(Math.round);
      const secondary = primary.map(
        channel => Math.round(Math.max(10, Math.min(220, (channel * .76) + 8)))
      );

      return {
        primary,
        secondary,
        confidence: clampNumber(.60 + (alpha * .18), .60, .78)
      };
    };

    const getSurfacePalette = () => {
      const candidates = [];
      const seen = new Set();

      const add = element => {
        if (!(element instanceof Element) || footer.contains(element) || seen.has(element)) return;
        seen.add(element);
        candidates.push(element);
      };

      /*
        Prefer the actual content directly above the footer. This works even when
        the footer starts well below the initial viewport.
      */
      const main =
        document.querySelector('#MainContent')
        || document.querySelector('main')
        || document.querySelector('[role="main"]');

      if (main) {
        const directChildren = [...main.children].reverse();

        directChildren.slice(0, 8).forEach(child => {
          const rect = child.getBoundingClientRect();

          if (rect.width > 100 && rect.height > 40) {
            add(child);

            [
              ...child.querySelectorAll(
                ':scope > section, :scope > article, [class*="hero"], [class*="banner"], [class*="aura"], [class*="books"]'
              )
            ].slice(0, 12).forEach(add);
          }
        });

        add(main);
      }

      /*
        When the footer is near/in the viewport, sample what is literally touching
        it as another strong source of truth.
      */
      const footerRect = footer.getBoundingClientRect();

      if (footerRect.top > 0 && footerRect.top < window.innerHeight + 140) {
        const yValues = [
          Math.max(4, footerRect.top - 4),
          Math.max(4, footerRect.top - 34),
          Math.max(4, footerRect.top - 82)
        ];

        const xValues = [
          Math.max(8, window.innerWidth * .12),
          window.innerWidth * .50,
          Math.min(window.innerWidth - 8, window.innerWidth * .88)
        ];

        yValues.forEach(y => {
          xValues.forEach(x => {
            document.elementsFromPoint(x, y).forEach(element => {
              if (!(element instanceof Element)) return;
              if (element.closest('header, footer, nav, aside, dialog')) return;

              add(
                element.closest(
                  '.shopify-section, section, article, [class*="hero"], [class*="banner"], [class*="aura"], [class*="books"]'
                ) || element
              );
            });
          });
        });
      }

      add(document.body);
      add(document.documentElement);

      for (const element of candidates) {
        const auraPalette = paletteFromAuraVariables(element);
        if (auraPalette) return { ...auraPalette, kind: 'aura-surface' };

        for (const pseudo of [null, '::before', '::after']) {
          let style;

          try {
            style = getComputedStyle(element, pseudo);
          } catch (error) {
            continue;
          }

          const gradientPalette = paletteFromCssGradient(style.backgroundImage);

          if (gradientPalette) {
            return {
              ...gradientPalette,
              confidence: Math.max(gradientPalette.confidence || 0, .72),
              kind: pseudo ? 'pseudo-gradient-surface' : 'gradient-surface'
            };
          }
        }

        let solidPalette = null;

        try {
          solidPalette = paletteFromCssColor(getComputedStyle(element).backgroundColor);
        } catch (error) {
          solidPalette = null;
        }

        if (solidPalette) {
          return {
            ...solidPalette,
            confidence: Math.max(solidPalette.confidence || 0, .68),
            kind: 'solid-surface'
          };
        }
      }

      return null;
    };

    const applyFooterPalette = (primary, secondary, mode = 'artwork') => {
      const midpoint = primary.map(
        (value, index) => Math.round((value * .52) + (secondary[index] * .48))
      );

      footer.style.setProperty('--footer-art-primary', primary.join(', '));
      footer.style.setProperty('--footer-art-mid', midpoint.join(', '));
      footer.style.setProperty('--footer-art-secondary', secondary.join(', '));

      if (mode === 'surface') {
        /*
          Surface-only pages need a little less black shielding than artwork pages,
          otherwise warm page colors collapse back toward neutral black.
        */
        footer.style.setProperty('--footer-dark-0', '.88');
        footer.style.setProperty('--footer-dark-18', '.83');
        footer.style.setProperty('--footer-dark-46', '.77');
        footer.style.setProperty('--footer-dark-72', '.70');
        footer.style.setProperty('--footer-dark-100', '.64');
        footer.style.setProperty('--footer-edge-dark', '.15');

        footer.classList.remove('has-artwork-tint');
        footer.classList.add('has-surface-tint');
        footer.dataset.footerPaletteMode = 'surface';
      } else {
        footer.style.setProperty('--footer-dark-0', '.93');
        footer.style.setProperty('--footer-dark-18', '.89');
        footer.style.setProperty('--footer-dark-46', '.84');
        footer.style.setProperty('--footer-dark-72', '.78');
        footer.style.setProperty('--footer-dark-100', '.72');
        footer.style.setProperty('--footer-edge-dark', '.20');

        footer.classList.remove('has-surface-tint');
        footer.classList.add('has-artwork-tint');
        footer.dataset.footerPaletteMode = 'artwork';
      }
    };

    const getArtworkCandidates = () => {
      const found = new Map();

      artworkSelectors.forEach(([selector, priority]) => {
        document.querySelectorAll(selector).forEach(image => {
          if (!(image instanceof HTMLImageElement) || footer.contains(image)) return;
          const existing = found.get(image) || 0;
          if (priority > existing) found.set(image, priority);
        });
      });

      if (!found.size) {
        document.querySelectorAll('main img').forEach(image => {
          if (!(image instanceof HTMLImageElement) || footer.contains(image)) return;
          found.set(image, 1200);
        });
      }

      const footerRect = footer.getBoundingClientRect();
      const footerApproaching = footerRect.top < (window.innerHeight * 2.2);
      const focusY = footerApproaching ? window.innerHeight * .62 : Math.min(window.innerHeight * .38, 360);

      return [...found.entries()]
        .map(([image, priority]) => {
          const rect = image.getBoundingClientRect();
          const source = image.currentSrc || image.src || '';
          if (!source || rect.width < 90 || rect.height < 90) return null;

          const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
          const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
          const visibleArea = width * height;
          const rawArea = Math.max(1, rect.width * rect.height);
          const visibleRatio = visibleArea / rawArea;
          const centerY = rect.top + (rect.height / 2);
          const distance = Math.abs(centerY - focusY);

          // Main release artwork should remain dominant. On catalog pages,
          // nearby visible covers naturally rise to the top as the user scrolls.
          const score = priority
            + Math.min(2200, rawArea / 110)
            + (visibleRatio * 1700)
            - (distance * 1.45);

          return { image, source, score };
        })
        .filter(Boolean)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3);
    };

    const extractPalette = image => {
      const canvas = document.createElement('canvas');
      const size = 40;
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;

      context.drawImage(image, 0, 0, size, size);
      const pixels = context.getImageData(0, 0, size, size).data;
      const buckets = new Map();

      for (let index = 0; index < pixels.length; index += 4) {
        if (pixels[index + 3] < 145) continue;

        const r = pixels[index];
        const g = pixels[index + 1];
        const b = pixels[index + 2];
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const saturation = max === 0 ? 0 : (max - min) / max;
        const luminance = (.2126 * r) + (.7152 * g) + (.0722 * b);
        if (luminance < 16 || luminance > 242) continue;

        const qr = Math.min(255, Math.round(r / 32) * 32);
        const qg = Math.min(255, Math.round(g / 32) * 32);
        const qb = Math.min(255, Math.round(b / 32) * 32);
        const key = `${qr},${qg},${qb}`;
        const bucket = buckets.get(key) || { score: 0, count: 0, r: 0, g: 0, b: 0 };
        const chromaBoost = .20 + (saturation * 4.6);
        const midtoneBoost = luminance > 30 && luminance < 228 ? 1.34 : .76;

        bucket.score += chromaBoost * midtoneBoost;
        bucket.count += 1;
        bucket.r += r;
        bucket.g += g;
        bucket.b += b;
        buckets.set(key, bucket);
      }

      const ranked = [...buckets.values()]
        .filter(bucket => bucket.count >= 2)
        .map(bucket => ({
          score: bucket.score,
          color: [
            Math.round(bucket.r / bucket.count),
            Math.round(bucket.g / bucket.count),
            Math.round(bucket.b / bucket.count)
          ]
        }))
        .sort((a, b) => b.score - a.score);

      if (!ranked.length) return null;

      const vividify = (color, strength = 1.9) => {
        const [r, g, b] = color;
        const luma = (.2126 * r) + (.7152 * g) + (.0722 * b);
        const boosted = [r, g, b].map(channel => luma + ((channel - luma) * strength));
        return boosted.map(channel => {
          const lifted = (channel * 1.08) + 5;
          return Math.round(Math.max(12, Math.min(248, lifted)));
        });
      };

      const distance = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
      const primary = vividify(ranked[0].color, 1.95);
      let secondary = primary;

      for (const candidate of ranked.slice(1, 16)) {
        const tuned = vividify(candidate.color, 1.85);
        if (distance(primary, tuned) > 72) {
          secondary = tuned;
          break;
        }
      }

      if (secondary === primary && ranked[1]) {
        secondary = vividify(ranked[1].color, 1.72);
      }

      return { primary, secondary };
    };

    const getImagePalette = (image, source) => {
      if (paletteCache.has(source)) return paletteCache.get(source);

      const promise = new Promise(resolve => {
        const finish = palette => resolve(palette || null);

        if (image.complete && image.naturalWidth > 0) {
          try {
            const direct = extractPalette(image);
            if (direct) {
              finish(direct);
              return;
            }
          } catch (error) {
            // Retry with an anonymous sampler below.
          }
        }

        const sampler = new Image();
        sampler.crossOrigin = 'anonymous';
        sampler.decoding = 'async';
        sampler.onload = () => {
          try {
            finish(extractPalette(sampler));
          } catch (error) {
            finish(null);
          }
        };
        sampler.onerror = () => finish(null);
        sampler.src = source;
      });

      paletteCache.set(source, promise);
      return promise;
    };

    const updateArtworkTint = async force => {
      const candidates = getArtworkCandidates();

      /*
        Preferred path: actual artwork still wins exactly as before.
      */
      if (candidates.length) {
        const sourceKey = candidates.map(item => item.source).join('|');

        if (!force && sourceKey === lastSources) return;
        lastSources = sourceKey;

        const currentToken = ++requestToken;
        const palettes = await Promise.all(
          candidates.map(item => getImagePalette(item.image, item.source))
        );

        if (currentToken !== requestToken) return;

        const usable = palettes.filter(Boolean);

        if (usable.length) {
          const weights = [.78, .16, .06];

          const blend = key => {
            let total = 0;
            const result = [0, 0, 0];

            usable.forEach((palette, index) => {
              const weight = weights[index] || .08;
              total += weight;
              result[0] += palette[key][0] * weight;
              result[1] += palette[key][1] * weight;
              result[2] += palette[key][2] * weight;
            });

            return result.map(value => Math.round(value / total));
          };

          applyFooterPalette(
            blend('primary'),
            blend('secondary'),
            'artwork'
          );

          return;
        }
      }

      /*
        No usable artwork: inherit the visual language of the page/section above.
      */
      const surface = getSurfacePalette();

      if (surface) {
        const surfaceKey =
          `surface:${surface.kind}:${surface.primary.join(',')}:${surface.secondary.join(',')}`;

        if (!force && surfaceKey === lastSources) return;
        lastSources = surfaceKey;

        applyFooterPalette(
          surface.primary,
          surface.secondary,
          'surface'
        );

        return;
      }

      /*
        Last-resort neutral state.
      */
      lastSources = '';
      footer.style.setProperty('--footer-art-primary', '92, 92, 92');
      footer.style.setProperty('--footer-art-secondary', '54, 54, 54');
      footer.style.setProperty('--footer-art-mid', '72, 72, 72');
      footer.style.setProperty('--footer-dark-0', '.93');
      footer.style.setProperty('--footer-dark-18', '.89');
      footer.style.setProperty('--footer-dark-46', '.84');
      footer.style.setProperty('--footer-dark-72', '.78');
      footer.style.setProperty('--footer-dark-100', '.72');
      footer.style.setProperty('--footer-edge-dark', '.20');
      footer.classList.remove('has-artwork-tint', 'has-surface-tint');
      delete footer.dataset.footerPaletteMode;
    };

    const scheduleTint = (force = false) => {
      if (updateQueued && !force) return;
      updateQueued = true;

      window.requestAnimationFrame(() => {
        updateQueued = false;
        updateArtworkTint(force);
      });
    };

    scheduleTint(true);
    window.addEventListener('load', () => scheduleTint(true), { once: true });
    window.addEventListener('scroll', () => scheduleTint(false), { passive: true });
    window.addEventListener('resize', () => scheduleTint(true), { passive: true });
  })();
